import { useState, useEffect, useCallback } from "react";
import { motion } from "framer-motion";
import { Lock, Plus, Pencil, Trash2, X, Save, MessageSquare, Image, Handshake, LogOut } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const tabs = [
  { id: "testimonials", label: "Testimonials", icon: MessageSquare },
  { id: "gallery", label: "Gallery", icon: Image },
  { id: "partners", label: "Partners", icon: Handshake },
];

export default function Admin() {
  const [password, setPassword] = useState("");
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [activeTab, setActiveTab] = useState("testimonials");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [showForm, setShowForm] = useState(false);

  const authHeaders = { "X-Admin-Password": password };

  const fetchItems = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/${activeTab}`);
      setItems(res.data);
    } catch (err) {
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  }, [activeTab]);

  useEffect(() => {
    if (isLoggedIn) {
      fetchItems();
      setShowForm(false);
      setEditingItem(null);
    }
  }, [activeTab, isLoggedIn, fetchItems]);

  const handleLogin = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/admin/login?password=${encodeURIComponent(password)}`);
      setIsLoggedIn(true);
      toast.success("Logged in successfully");
    } catch {
      toast.error("Invalid password");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this item?")) return;
    try {
      await axios.delete(`${API}/admin/${activeTab}/${id}`, { headers: authHeaders });
      toast.success("Deleted successfully");
      fetchItems();
    } catch {
      toast.error("Failed to delete");
    }
  };

  const handleSave = async (formData) => {
    try {
      if (editingItem) {
        await axios.put(`${API}/admin/${activeTab}/${editingItem.id}`, formData, { headers: authHeaders });
        toast.success("Updated successfully");
      } else {
        await axios.post(`${API}/admin/${activeTab}`, formData, { headers: authHeaders });
        toast.success("Created successfully");
      }
      setShowForm(false);
      setEditingItem(null);
      fetchItems();
    } catch {
      toast.error("Failed to save");
    }
  };

  const startEdit = (item) => {
    setEditingItem(item);
    setShowForm(true);
  };

  const startNew = () => {
    setEditingItem(null);
    setShowForm(true);
  };

  if (!isLoggedIn) {
    return (
      <div data-testid="admin-login-page" className="pt-20 min-h-screen bg-slate-50 flex items-center justify-center">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white p-8 rounded-2xl shadow-lg max-w-md w-full mx-4"
        >
          <div className="text-center mb-8">
            <div className="w-16 h-16 bg-[#0033A0] rounded-2xl flex items-center justify-center mx-auto mb-4">
              <Lock className="w-8 h-8 text-white" />
            </div>
            <h1 className="text-2xl font-bold text-[#0033A0]">Admin Panel</h1>
            <p className="text-slate-500 mt-2">Enter your admin password to continue</p>
          </div>
          <form onSubmit={handleLogin} className="space-y-4">
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Admin password"
              data-testid="admin-password-input"
              className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-[#0033A0] focus:ring-2 focus:ring-[#0033A0]/20 transition-all"
              autoFocus
            />
            <button
              type="submit"
              data-testid="admin-login-btn"
              className="btn-primary w-full"
            >
              Log In
            </button>
          </form>
        </motion.div>
      </div>
    );
  }

  return (
    <div data-testid="admin-dashboard" className="pt-20 min-h-screen bg-slate-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-[#0033A0]">Admin Panel</h1>
            <p className="text-slate-500">Manage your website content</p>
          </div>
          <button
            onClick={() => { setIsLoggedIn(false); setPassword(""); }}
            data-testid="admin-logout-btn"
            className="flex items-center gap-2 text-slate-600 hover:text-red-600 transition-colors"
          >
            <LogOut size={18} />
            Logout
          </button>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-8 bg-white p-2 rounded-xl shadow-sm">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              data-testid={`admin-tab-${tab.id}`}
              className={`flex items-center gap-2 px-5 py-3 rounded-lg font-semibold transition-all flex-1 justify-center ${
                activeTab === tab.id
                  ? "bg-[#0033A0] text-white"
                  : "text-slate-600 hover:bg-slate-100"
              }`}
            >
              <tab.icon size={18} />
              {tab.label}
            </button>
          ))}
        </div>

        {/* Action Bar */}
        <div className="flex justify-between items-center mb-6">
          <p className="text-slate-600">{items.length} {activeTab}</p>
          <button
            onClick={startNew}
            data-testid="admin-add-btn"
            className="btn-primary flex items-center gap-2"
          >
            <Plus size={18} />
            Add New
          </button>
        </div>

        {/* Form Modal */}
        {showForm && (
          <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={() => setShowForm(false)}>
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="bg-white rounded-2xl p-6 max-w-lg w-full max-h-[80vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
              data-testid="admin-form-modal"
            >
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold text-[#0033A0]">
                  {editingItem ? "Edit" : "Add"} {activeTab.slice(0, -1)}
                </h2>
                <button onClick={() => setShowForm(false)} className="text-slate-400 hover:text-slate-600">
                  <X size={20} />
                </button>
              </div>
              <ItemForm
                type={activeTab}
                item={editingItem}
                onSave={handleSave}
                onCancel={() => setShowForm(false)}
              />
            </motion.div>
          </div>
        )}

        {/* Items List */}
        {loading ? (
          <div className="flex justify-center py-20">
            <div className="w-10 h-10 border-4 border-[#0033A0] border-t-transparent rounded-full animate-spin" />
          </div>
        ) : items.length === 0 ? (
          <div className="text-center py-20 text-slate-500">
            No {activeTab} yet. Click "Add New" to get started.
          </div>
        ) : (
          <div className="grid gap-4">
            {items.map((item) => (
              <ItemCard
                key={item.id}
                item={item}
                type={activeTab}
                onEdit={() => startEdit(item)}
                onDelete={() => handleDelete(item.id)}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function ItemCard({ item, type, onEdit, onDelete }) {
  return (
    <div
      className="bg-white rounded-xl p-4 shadow-sm border border-slate-100 flex items-center gap-4 hover:shadow-md transition-all"
      data-testid={`admin-item-${item.id}`}
    >
      {/* Thumbnail */}
      {type === "gallery" && item.image_url && (
        <img src={item.image_url} alt={item.title} className="w-16 h-16 rounded-lg object-cover flex-shrink-0" />
      )}
      {type === "partners" && item.logo_url && (
        <img src={item.logo_url} alt={item.name} className="w-16 h-16 rounded-lg object-contain flex-shrink-0 bg-slate-50 p-1" />
      )}
      {type === "partners" && !item.logo_url && (
        <div className="w-16 h-16 rounded-lg bg-[#0033A0] flex items-center justify-center flex-shrink-0">
          <Handshake className="w-6 h-6 text-[#FFC107]" />
        </div>
      )}
      {type === "testimonials" && (
        <div className="w-16 h-16 rounded-lg bg-[#0033A0] flex items-center justify-center flex-shrink-0">
          <span className="text-[#FFC107] font-bold text-lg">{item.name?.charAt(0)}</span>
        </div>
      )}

      {/* Content */}
      <div className="flex-1 min-w-0">
        <p className="font-bold text-[#0033A0] truncate">
          {item.title || item.name}
        </p>
        <p className="text-sm text-slate-500 truncate">
          {type === "testimonials" && `${item.role} — "${item.content?.substring(0, 60)}..."`}
          {type === "gallery" && `${item.category} — ${item.description || ""}`}
          {type === "partners" && (item.logo_url ? "Has logo" : "No logo")}
        </p>
      </div>

      {/* Actions */}
      <div className="flex gap-2 flex-shrink-0">
        <button
          onClick={onEdit}
          data-testid={`admin-edit-${item.id}`}
          className="w-9 h-9 rounded-lg bg-slate-100 hover:bg-[#FFC107] flex items-center justify-center transition-colors"
        >
          <Pencil size={16} className="text-slate-600" />
        </button>
        <button
          onClick={onDelete}
          data-testid={`admin-delete-${item.id}`}
          className="w-9 h-9 rounded-lg bg-slate-100 hover:bg-red-100 flex items-center justify-center transition-colors"
        >
          <Trash2 size={16} className="text-slate-600 hover:text-red-600" />
        </button>
      </div>
    </div>
  );
}

function ItemForm({ type, item, onSave, onCancel }) {
  const [formData, setFormData] = useState(() => {
    if (item) {
      if (type === "testimonials") return { name: item.name, role: item.role, content: item.content };
      if (type === "gallery") return { title: item.title, description: item.description || "", image_url: item.image_url, category: item.category };
      if (type === "partners") return { name: item.name, logo_url: item.logo_url || "" };
    }
    if (type === "testimonials") return { name: "", role: "", content: "" };
    if (type === "gallery") return { title: "", description: "", image_url: "", category: "community" };
    return { name: "", logo_url: "" };
  });

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const data = { ...formData };
    if (type === "partners" && !data.logo_url) data.logo_url = null;
    onSave(data);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4" data-testid="admin-item-form">
      {type === "testimonials" && (
        <>
          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-1">Name</label>
            <input name="name" value={formData.name} onChange={handleChange} required
              className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-[#0033A0] focus:ring-2 focus:ring-[#0033A0]/20" />
          </div>
          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-1">Role</label>
            <input name="role" value={formData.role} onChange={handleChange} required
              className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-[#0033A0] focus:ring-2 focus:ring-[#0033A0]/20" />
          </div>
          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-1">Testimonial</label>
            <textarea name="content" value={formData.content} onChange={handleChange} required rows={4}
              className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-[#0033A0] focus:ring-2 focus:ring-[#0033A0]/20 resize-none" />
          </div>
        </>
      )}

      {type === "gallery" && (
        <>
          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-1">Title</label>
            <input name="title" value={formData.title} onChange={handleChange} required
              className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-[#0033A0] focus:ring-2 focus:ring-[#0033A0]/20" />
          </div>
          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-1">Description</label>
            <input name="description" value={formData.description} onChange={handleChange}
              className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-[#0033A0] focus:ring-2 focus:ring-[#0033A0]/20" />
          </div>
          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-1">Image URL</label>
            <input name="image_url" value={formData.image_url} onChange={handleChange} required
              className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-[#0033A0] focus:ring-2 focus:ring-[#0033A0]/20" />
            {formData.image_url && (
              <img src={formData.image_url} alt="Preview" className="mt-2 h-32 rounded-lg object-cover" />
            )}
          </div>
          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-1">Category</label>
            <select name="category" value={formData.category} onChange={handleChange}
              className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-[#0033A0] focus:ring-2 focus:ring-[#0033A0]/20">
              <option value="community">Community</option>
              <option value="culinary">Culinary</option>
              <option value="construction">Construction</option>
              <option value="landscaping">Landscaping</option>
              <option value="leadership">Leadership</option>
            </select>
          </div>
        </>
      )}

      {type === "partners" && (
        <>
          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-1">Partner Name</label>
            <input name="name" value={formData.name} onChange={handleChange} required
              className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-[#0033A0] focus:ring-2 focus:ring-[#0033A0]/20" />
          </div>
          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-1">Logo URL (optional)</label>
            <input name="logo_url" value={formData.logo_url} onChange={handleChange}
              className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-[#0033A0] focus:ring-2 focus:ring-[#0033A0]/20" />
            {formData.logo_url && (
              <img src={formData.logo_url} alt="Preview" className="mt-2 h-20 rounded-lg object-contain" />
            )}
          </div>
        </>
      )}

      <div className="flex gap-3 pt-4">
        <button type="submit" data-testid="admin-save-btn" className="btn-primary flex-1 flex items-center justify-center gap-2">
          <Save size={18} />
          {item ? "Update" : "Create"}
        </button>
        <button type="button" onClick={onCancel} className="px-6 py-3 rounded-full border border-slate-200 text-slate-600 hover:bg-slate-50 transition-all">
          Cancel
        </button>
      </div>
    </form>
  );
}
